const toCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isRecord = (value) => value && typeof value === 'object' && !Array.isArray(value);

export const EMPTY_LEADERSHIP_PIPELINE = {
  statCards: {
    totalProjects: 0,
    totalProjectsThisFy: 0,
    totalProjectsGoLiveThisFy: 0,
    active: 0,
    atRisk: 0,
    completed: 0,
    completedRegisteredThisFy: 0,
  },
  byProduct: [],
  byEngagementType: [],
  byRegion: [],
  byRegionCompleted: [],
  byRegionCompletedQuarter: [],
  regionQuarterLegend: ['Q1', 'Q2', 'Q3', 'Q4'],
  byCompanyStatus: [],
  companyStatusLegend: [],
  atRiskProjects: [],
  recentlyCompletedProjects: [],
};

const normalizeStatCards = (statCards = {}) => ({
  totalProjects: toCount(statCards.totalProjects ?? statCards.total_projects),
  totalProjectsThisFy: toCount(
    statCards.totalProjectsThisFy ?? statCards.total_projects_this_fy,
  ),
  totalProjectsGoLiveThisFy: toCount(
    statCards.totalProjectsGoLiveThisFy ?? statCards.total_projects_go_live_this_fy,
  ),
  active: toCount(statCards.active),
  atRisk: toCount(statCards.atRisk ?? statCards.at_risk),
  completed: toCount(statCards.completed),
  completedRegisteredThisFy: toCount(
    statCards.completedRegisteredThisFy ?? statCards.completed_registered_this_fy,
  ),
});

const normalizeByProduct = (items = []) => (
  (Array.isArray(items) ? items : []).map((item) => ({
    product: item.product || item.name || '-',
    count: toCount(item.count),
  }))
);

const normalizeByEngagementType = (items = []) => (
  (Array.isArray(items) ? items : []).map((item) => ({
    engagementType: item.engagementType || item.engagement_type || item.name || '-',
    count: toCount(item.count),
  }))
);

const normalizeByRegion = (items = []) => (
  (Array.isArray(items) ? items : []).map((item) => ({
    region: item.region || item.name || '-',
    count: toCount(item.count),
  }))
);

export const PARTNER_LEADERSHIP_REGIONS = ['APJ', 'EMEA', 'NA', 'LATAM'];

export const normalizeLeadershipRegionCode = (region) => {
  const trimmed = String(region || '').trim();
  if (!trimmed) {
    return '-';
  }

  const key = trimmed.toUpperCase();
  if (key === 'US') {
    return 'NA';
  }
  if (PARTNER_LEADERSHIP_REGIONS.includes(key)) {
    return key;
  }

  return trimmed;
};

const mergeLeadershipRegionCatalog = (items = []) => {
  const normalized = normalizeByRegion(items);
  const countByRegion = normalized.reduce((accumulator, item) => {
    const region = normalizeLeadershipRegionCode(item.region);
    if (region === '-') {
      return accumulator;
    }
    accumulator[region] = (accumulator[region] || 0) + item.count;
    return accumulator;
  }, {});

  const catalogRows = PARTNER_LEADERSHIP_REGIONS.map((region) => ({
    region,
    count: countByRegion[region] ?? 0,
  }));

  const extraRows = normalized
    .map((item) => ({
      region: normalizeLeadershipRegionCode(item.region),
      count: item.count,
    }))
    .filter(
      (item) => !PARTNER_LEADERSHIP_REGIONS.includes(item.region) && item.count > 0,
    );

  return [...catalogRows, ...extraRows];
};

const normalizeByCompanyStatus = (items = []) => (
  (Array.isArray(items) ? items : []).map((item) => ({
    companyName: item.companyName || item.name || '-',
    total: toCount(item.total ?? item.count),
    statuses: (Array.isArray(item.statuses) ? item.statuses : []).map((statusItem) => ({
      status: statusItem.status || statusItem.name || 'Unknown',
      count: toCount(statusItem.count),
    })),
  }))
);

const LEADERSHIP_FY_QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

const normalizeByRegionCompletedQuarter = (items = []) => {
  const normalized = (Array.isArray(items) ? items : []).map((item) => {
    const region = normalizeLeadershipRegionCode(item.region || item.name || '-');
    const quartersSource = Array.isArray(item.quarters)
      ? item.quarters
      : (Array.isArray(item.statuses) ? item.statuses : []);
    const quarterCounts = LEADERSHIP_FY_QUARTERS.reduce((accumulator, quarter) => {
      accumulator[quarter] = 0;
      return accumulator;
    }, {});

    quartersSource.forEach((quarterItem) => {
      const quarter = String(
        quarterItem.quarter || quarterItem.status || quarterItem.name || '',
      ).trim().toUpperCase();
      if (!LEADERSHIP_FY_QUARTERS.includes(quarter)) {
        return;
      }
      quarterCounts[quarter] += toCount(quarterItem.count);
    });

    const quarters = LEADERSHIP_FY_QUARTERS
      .filter((quarter) => quarterCounts[quarter] > 0)
      .map((quarter) => ({
        status: quarter,
        count: quarterCounts[quarter],
      }));
    const total = toCount(item.total)
      || quarters.reduce((sum, quarterItem) => sum + quarterItem.count, 0);

    return {
      region,
      companyName: region,
      total,
      statuses: quarters,
    };
  });

  const byRegion = normalized.reduce((accumulator, item) => {
    if (!item.region || item.region === '-') {
      return accumulator;
    }
    const existing = accumulator[item.region] || {
      region: item.region,
      companyName: item.region,
      total: 0,
      statuses: LEADERSHIP_FY_QUARTERS.map((quarter) => ({ status: quarter, count: 0 })),
    };
    const countByQuarter = existing.statuses.reduce((map, statusItem) => {
      map[statusItem.status] = statusItem.count;
      return map;
    }, {});
    item.statuses.forEach((statusItem) => {
      countByQuarter[statusItem.status] = (countByQuarter[statusItem.status] || 0) + statusItem.count;
    });
    const statuses = LEADERSHIP_FY_QUARTERS
      .filter((quarter) => countByQuarter[quarter] > 0)
      .map((quarter) => ({ status: quarter, count: countByQuarter[quarter] }));
    accumulator[item.region] = {
      region: item.region,
      companyName: item.region,
      total: statuses.reduce((sum, statusItem) => sum + statusItem.count, 0),
      statuses,
    };
    return accumulator;
  }, {});

  const catalogRows = PARTNER_LEADERSHIP_REGIONS.map((region) => (
    byRegion[region] || {
      region,
      companyName: region,
      total: 0,
      statuses: [],
    }
  ));
  const extraRows = Object.values(byRegion).filter(
    (item) => !PARTNER_LEADERSHIP_REGIONS.includes(item.region) && item.total > 0,
  );

  return [...catalogRows, ...extraRows];
};

const normalizeProjectList = (items = []) => (
  (Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    productsInScope: Array.isArray(item.productsInScope) ? item.productsInScope : [],
  }))
);

export const normalizeLeadershipDrilldownResponse = (data) => {
  if (!data || typeof data !== 'object') {
    return {
      projects: [],
      total: 0,
      filter: {},
    };
  }

  const payload = isRecord(data.data) ? { ...data, ...data.data } : data;

  return {
    projects: normalizeProjectList(payload.projects),
    total: toCount(payload.total),
    filter: isRecord(payload.filter) ? payload.filter : {},
  };
};

export const buildLeadershipDrilldownTitle = (filter, regionLabels = {}) => {
  if (!filter?.dimension || !filter?.value) {
    return 'Project list';
  }

  const displayValue = filter.dimension === 'region'
    ? (regionLabels[filter.value] || filter.value)
    : filter.value;

  if (filter.dimension === 'company' && filter.status) {
    return `${displayValue} · ${filter.status}`;
  }

  if (filter.dimension === 'product') {
    return displayValue;
  }

  if (filter.dimension === 'region') {
    return displayValue;
  }

  return displayValue;
};

export const normalizeLeadershipPipelineResponse = (data) => {
  if (!data || typeof data !== 'object') {
    return EMPTY_LEADERSHIP_PIPELINE;
  }

  const payload = isRecord(data.data) ? { ...data, ...data.data } : data;

  const regionQuarterLegend = payload.regionQuarterLegend || payload.region_quarter_legend;

  return {
    statCards: normalizeStatCards(payload.statCards || payload.stat_cards),
    byProduct: normalizeByProduct(payload.byProduct || payload.by_product),
    byEngagementType: normalizeByEngagementType(
      payload.byEngagementType || payload.by_engagement_type,
    ),
    byRegion: mergeLeadershipRegionCatalog(payload.byRegion || payload.by_region),
    byRegionCompleted: mergeLeadershipRegionCatalog(
      payload.byRegionCompleted || payload.by_region_completed,
    ),
    byRegionCompletedQuarter: normalizeByRegionCompletedQuarter(
      payload.byRegionCompletedQuarter || payload.by_region_completed_quarter,
    ),
    regionQuarterLegend: Array.isArray(regionQuarterLegend) && regionQuarterLegend.length
      ? regionQuarterLegend
      : ['Q1', 'Q2', 'Q3', 'Q4'],
    byCompanyStatus: normalizeByCompanyStatus(payload.byCompanyStatus || payload.by_company_status),
    companyStatusLegend: Array.isArray(payload.companyStatusLegend || payload.company_status_legend)
      ? (payload.companyStatusLegend || payload.company_status_legend)
      : [],
    atRiskProjects: normalizeProjectList(payload.atRiskProjects || payload.at_risk_projects),
    recentlyCompletedProjects: normalizeProjectList(
      payload.recentlyCompletedProjects || payload.recently_completed_projects,
    ),
  };
};
